/**
 * Invoice mutation hooks using generic factory
 */

import { classifyBillingRunFailure } from '@/components/commercial-ops/billing-run-failure'
import { invoiceApi } from '@/lib/api/invoices.api'
import { getOrgPath } from '@/utils/org-paths'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import type {
  CreateBillingRunPayload,
  CreateInvoice,
  Invoice,
  UpdateInvoiceInput,
} from '@scrambl/shared'

import { BILLING_AUTHORIZATIONS_QUERY_KEY } from '../queries/useBillingAuthorizationsQuery'
import { useInvalidateClientBillingRun } from '../queries/useClientBillingRunQuery'
import { queryKeys } from '../query-keys'
import { createResourceMutations } from './createResourceMutations'

// Invoice totals, status and linkage drive client-authorization consumption
// (recomputed server-side), so every invoice write refreshes those cards.
const invalidateAuthorizations = {
  additionalInvalidations: [() => BILLING_AUTHORIZATIONS_QUERY_KEY],
}

const mutations = createResourceMutations<
  Invoice,
  CreateInvoice,
  UpdateInvoiceInput
>({
  resourceName: 'Invoice',
  resourceNameSingular: 'invoice',
  queryKeyFactory: queryKeys.invoices,
  api: {
    create: (data: CreateInvoice) => invoiceApi.createInvoice(data),
    update: (id: string, data: UpdateInvoiceInput) =>
      invoiceApi.updateInvoice(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateInvoiceInput
      return invoiceApi.updateInvoice(id, updateData)
    },
    delete: (id: string) => invoiceApi.deleteInvoice(id),
  },
  invalidation: {
    onCreate: invalidateAuthorizations,
    onUpdate: invalidateAuthorizations,
    onDelete: invalidateAuthorizations,
  },
  enableRealtime: false,
})

export const useCreateInvoiceMutation = mutations.useCreateMutation
export const useUpdateInvoiceMutation = mutations.useUpdateMutation
export const useUpdateInvoiceFieldMutation = mutations.useUpdateFieldMutation!
export const useDeleteInvoiceMutation = mutations.useDeleteMutation

/**
 * Client billing run — creates one invoice for the client, or one per project,
 * in a single action. The whole run is transactional server-side: either every
 * invoice exists and every hour is claimed, or none of it happened.
 */
export function useCreateClientBillingRunMutation(entityId: string) {
  const queryClient = useQueryClient()
  const invalidateBillingRun = useInvalidateClientBillingRun()
  const navigate = useNavigate()
  const { orgSlug = '' } = useParams<{ orgSlug: string }>()

  // The client's draft invoices: where a multi-invoice run lands, and where a
  // run whose outcome is unknown can be checked.
  const clientInvoicesPath = getOrgPath(
    orgSlug,
    `/invoices?entityId=${encodeURIComponent(entityId)}&status=DRAFT`,
  )
  const viewClientInvoices = {
    label: 'View invoices',
    onClick: () => navigate(clientInvoicesPath),
  }

  return useMutation({
    mutationFn: (payload: CreateBillingRunPayload) =>
      invoiceApi.createClientBillingRun(entityId, payload),
    onSuccess: result => {
      const count = result.invoices.length
      if (count === 1) {
        toast.success(
          `Draft invoice ${result.invoices[0]!.invoiceNumber} created`,
        )
      } else {
        toast.success(`${count} draft invoices created`, {
          action: viewClientInvoices,
        })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() })
      // The run stamps time entries AND expenses as invoiced, so any expense
      // list still on screen is now showing a stale billing state.
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
      queryClient.invalidateQueries({
        queryKey: BILLING_AUTHORIZATIONS_QUERY_KEY,
      })
      void invalidateBillingRun(entityId)
    },
    onError: (error: unknown) => {
      // Whatever failed, the preview on screen may no longer be the truth: a
      // 409 means work moved under it, and a lost response may hide a run that
      // succeeded. Refetch rather than leave a stale selection to retry into.
      void invalidateBillingRun(entityId)
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })

      const failure = classifyBillingRunFailure(error)
      if (failure.kind === 'already-invoiced') {
        const only = failure.invoices.length === 1 ? failure.invoices[0] : null
        toast.error(failure.message, {
          duration: 10_000,
          action: only
            ? {
                label: 'Open invoice',
                onClick: () =>
                  navigate(getOrgPath(orgSlug, `/invoices/${only.invoiceId}`)),
              }
            : viewClientInvoices,
        })
        return
      }
      if (failure.kind === 'unknown-outcome') {
        toast.error(failure.message, {
          duration: 10_000,
          action: viewClientInvoices,
        })
        return
      }
      toast.error(failure.message)
    },
  })
}

/**
 * Void a sent invoice (SD-160). Voiding frees the time and expenses the
 * invoice billed, so expense lists refresh too.
 */
export function useVoidInvoiceMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      invoiceApi.voidInvoice(id, reason),
    onSuccess: invoice => {
      toast.success(
        invoice.invoiceNumber
          ? `Invoice ${invoice.invoiceNumber} voided`
          : 'Invoice voided',
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.detail(invoice.id),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all })
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to void invoice',
      )
    },
  })
}

/**
 * Bulk delete invoices mutation hook
 */
export function useBulkDeleteInvoicesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => invoiceApi.bulkDeleteInvoices(ids),
    onSuccess: result => {
      toast.success(
        `${result.deletedCount} invoice${result.deletedCount === 1 ? '' : 's'} deleted`,
      )
      // Invalidate invoices list
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() })
      queryClient.invalidateQueries({
        queryKey: BILLING_AUTHORIZATIONS_QUERY_KEY,
      })
    },
    onError: error => {
      // The server explains refusals (e.g. only drafts can be deleted).
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to delete invoices',
      )
      console.error('Bulk delete invoices error:', error)
    },
  })
}
